import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, Clock, Sparkles, Moon, Sun, Monitor, UserMinus, Palette, Type } from 'lucide-react';
import { AppSettings, ThemeMode } from '../../types';
import { PRESET_PALETTES } from '../../utils/colors';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onUpdateSettings: (updates: Partial<AppSettings>) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onUpdateSettings,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            padding: '1rem',
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="glass-panel"
            style={{
              width: '100%',
              maxWidth: '520px',
              padding: 'clamp(1rem, 4vw, 1.75rem)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '20px',
              maxHeight: 'min(90vh, 90dvh)',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.25rem',
              }}
            >
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Application Settings</h3>
              <button onClick={onClose} className="btn-ghost" style={{ padding: '6px' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>
              {/* Theme Selector */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Appearance Theme
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                  {(['dark', 'light', 'system'] as ThemeMode[]).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => onUpdateSettings({ theme: mode })}
                      style={{
                        padding: '7px 4px',
                        borderRadius: '10px',
                        border:
                          settings.theme === mode
                            ? '2px solid var(--accent-primary)'
                            : '1px solid var(--border-subtle)',
                        background:
                          settings.theme === mode ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-surface-elevated)',
                        color: settings.theme === mode ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        textTransform: 'capitalize',
                        minWidth: 0,
                      }}
                    >
                      {mode === 'dark' && <Moon size={15} />}
                      {mode === 'light' && <Sun size={15} />}
                      {mode === 'system' && <Monitor size={15} />}
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sound Settings */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Volume2 size={16} color="var(--accent-primary)" />
                    Sound Effects
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.soundEnabled}
                    onChange={(e) => onUpdateSettings({ soundEnabled: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                </div>

                {settings.soundEnabled && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                      <span>Master Volume</span>
                      <span>{Math.round(settings.masterVolume * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={settings.masterVolume}
                      onChange={(e) => onUpdateSettings({ masterVolume: parseFloat(e.target.value) })}
                      style={{ width: '100%', cursor: 'pointer' }}
                    />
                  </div>
                )}
              </div>

              {/* Wheel Name Font Size Setting */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Type size={16} color="var(--accent-primary)" />
                    Wheel Name Font Size
                  </label>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: 'var(--accent-primary)',
                      padding: '2px 8px',
                      background: 'rgba(99, 102, 241, 0.15)',
                      borderRadius: '6px',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                    }}
                  >
                    {settings.wheelNameFontSize ?? 24} px
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="50"
                  step="1"
                  value={settings.wheelNameFontSize ?? 24}
                  onChange={(e) => onUpdateSettings({ wheelNameFontSize: parseInt(e.target.value, 10) })}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                  <span>Small (10px)</span>
                  <span>Default (24px)</span>
                  <span>Large (50px)</span>
                </div>
              </div>

              {/* Default Spin Duration */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  <Clock size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                  Default Spin Duration
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                  {[3, 5, 8, 10].map((dur) => (
                    <button
                      key={dur}
                      onClick={() => onUpdateSettings({ defaultSpinDuration: dur })}
                      style={{
                        padding: '7px 4px',
                        borderRadius: '10px',
                        border:
                          settings.defaultSpinDuration === dur
                            ? '2px solid var(--accent-primary)'
                            : '1px solid var(--border-subtle)',
                        background:
                          settings.defaultSpinDuration === dur
                            ? 'rgba(99, 102, 241, 0.15)'
                            : 'var(--bg-surface-elevated)',
                        color: settings.defaultSpinDuration === dur ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        minWidth: 0,
                      }}
                    >
                      {dur}s
                    </button>
                  ))}
                </div>
              </div>

              {/* Winner Options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <UserMinus size={16} />
                      Remove Winner After Spin
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Automatically remove the winner segment from wheel upon winning
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.removeWinnerAfterSpin}
                    onChange={(e) => onUpdateSettings({ removeWinnerAfterSpin: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Sparkles size={16} />
                      Confetti & Visual Effects
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Trigger confetti cannons and particle fireworks
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.confettiEnabled}
                    onChange={(e) => onUpdateSettings({ confettiEnabled: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                </div>
              </div>

              {/* Default Color Theme */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  <Palette size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                  Default Palette
                </label>
                <select
                  value={settings.colorPalette}
                  onChange={(e) => onUpdateSettings({ colorPalette: e.target.value })}
                >
                  {PRESET_PALETTES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '1.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} className="btn-primary" style={{ padding: '0.65rem 1.5rem' }}>
                Done
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
